/**
 * Environment Configuration
 *
 * Zod-validated import.meta.env with defaults. Fails fast at module load
 * with a readable message when the environment is misconfigured.
 * VITE_API_BASE_URL is the single canonical WordPress REST API base URL.
 */

import { z } from 'zod';

/** 'true'/'false' string flag -> boolean, with default */
const boolFlag = (def: boolean) =>
  z.enum(['true', 'false']).default(def ? 'true' : 'false').transform((v) => v === 'true');

/** Optional public URL: empty string (unset) or an http(s) URL. */
const optionalUrl = z
  .string()
  .default('')
  .refine((v) => v === '' || /^https?:\/\/.+/.test(v), { message: 'must be empty or a valid http(s) URL' });

export const envSchema = z.looseObject({
  // WordPress / CMS — CANONICAL API base URL (decision D4). Empty string is
  // allowed and means "no WordPress" — pages render their coded content only.
  VITE_API_BASE_URL: z
    .string()
    .default('https://api.kgprodesign.com/kgpro/wp-json')
    .refine((v) => v === '' || /^https?:\/\/.+/.test(v), {
      message: 'must be empty (no WordPress) or a valid http(s) URL',
    }),
  // REST base of the site's WordPress grouping taxonomy (wordpress/content-models.json
  // `taxonomy.rest_base`) — used to filter photos/testimonials by term.
  VITE_WP_GROUPING_TAXONOMY: z
    .string()
    .regex(/^[a-z][a-z0-9_-]*$/, 'must be a WordPress REST base (lowercase slug)')
    .default('topics'),
  // Public social profile URLs (Footer SocialLinks). Unset = not shown.
  VITE_SOCIAL_FACEBOOK: optionalUrl,
  VITE_SOCIAL_INSTAGRAM: optionalUrl,
  VITE_SOCIAL_X: optionalUrl,
  VITE_SOCIAL_LINKEDIN: optionalUrl,
  VITE_SOCIAL_YOUTUBE: optionalUrl,
  VITE_CLIENT_THEME: z.string().default('default'),
  VITE_BLOG_NAME: z.string().default('Blog'),
  VITE_SITE_NAME: z.string().default('KG ProDesign'),
  VITE_MENU_CONFIG: z.string().default(''),
  VITE_HERO_HEADLINE: z.string().optional(),
  // Feature flags: dark mode is the only BUILD-level flag (ThemeProvider);
  // the others are runtime settings (features.* via /api/config/public).
  VITE_ENABLE_DARK_MODE: boolFlag(true),
});

export type Env = z.infer<typeof envSchema>;

const parsed = envSchema.safeParse(import.meta.env);
if (!parsed.success) {
  // Fail fast with a readable message
  throw new Error(`[config/env] Invalid environment configuration:\n${z.prettifyError(parsed.error)}`);
}
export const env: Env = parsed.data;
